import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { useSelector } from "react-redux";

const MeetingManagement = () => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  const { currentUser } = useSelector(state => state.auth);
  const email = currentUser?.email;
  const meetingAppUrl = process.env.REACT_APP_MEETING_APP_URL;

  const meetingOrigin = useMemo(() => {
    if (!meetingAppUrl) return null;
    try {
      return new URL(meetingAppUrl).origin;
    } catch (_) {
      return null;
    }
  }, [meetingAppUrl]);

  // Đọc path 1 LẦN khi mount. SYNC_PATH sau đó chỉ ghi localStorage cho lần
  // mở sau — nếu src tính lại mỗi render, bất kỳ re-render nào của Portal
  // (resize, state.auth đổi) sẽ gán src mới → iframe reload giữa chừng thao
  // tác của user (cùng lỗi HRM Hub đã sửa).
  const [initialPath] = useState(() => {
    try {
      return localStorage.getItem("meeting_app_path") || "/";
    } catch (_) {
      return "/";
    }
  });

  const fullIframeUrl = useMemo(() => {
    if (!email || !meetingAppUrl) return "";
    const separator = initialPath.includes("?") ? "&" : "?";
    const queryParams = `useHeader=false&OpendNav=false&Language=en&showLogo=false&email=${encodeURIComponent(email)}`;
    return `${meetingAppUrl}${initialPath}${separator}${queryParams}`;
  }, [email, meetingAppUrl, initialPath]);

  useEffect(() => {
    const calculateHeight = () => {
      // Đo navbar top
      const navbar =
        document.querySelector(".ptl-navbar-top") ||
        document.querySelector("nav.navbar") ||
        document.querySelector('[class*="navbar-top"]');
      const footer =
        document.querySelector("footer") ||
        document.querySelector('[class*="footer"]');

      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;

      const available = window.innerHeight - navbarHeight - footerHeight - 20;
      setIframeHeight(available);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  const sendCrmEmail = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !email || !meetingOrigin) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "SET_CRM_EMAIL", email },
      meetingOrigin
    );
  }, [email, meetingOrigin]);

  useEffect(() => {
    if (!meetingOrigin) return;
    const handleMessage = event => {
      if (event.origin !== meetingOrigin) return;
      const { type, path } = event.data || {};
      if (type === "SYNC_PATH" && path) {
        try {
          localStorage.setItem("meeting_app_path", path);
        } catch (_) {
          // storage bị chặn → chỉ mất khả năng restore path lần sau
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [meetingOrigin]);

  if (!email) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-items-center tw-justify-center tw-bg-slate-50"
      >
        <div className="tw-text-center tw-p-5">
          <h4 className="tw-text-slate-500 tw-mb-3">Authentication Required</h4>
          <p className="tw-text-slate-500">
            Please login again to access the Meeting Room system.
          </p>
        </div>
      </div>
    );
  }

  if (!fullIframeUrl) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-4"
      >
        <div className="tw-text-red-600 tw-mb-3">
          <FaExclamationTriangle size={48} />
        </div>
        <h4 className="tw-font-bold">Meeting link not found</h4>
        <p className="tw-text-slate-500">
          Please check the link again or contact the administrator.
        </p>
        <button
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-text-sm tw-cursor-pointer tw-border-0 tw-mt-2"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="tw-p-1 tw-bg-white tw-rounded-md"
      style={{ width: "100%", height: iframeHeight || "80vh" }}
    >
      <iframe
        ref={iframeRef}
        src={fullIframeUrl}
        title="Meeting Management App"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block"
        }}
        allowFullScreen
        onLoad={sendCrmEmail}
      />
    </div>
  );
};

export default MeetingManagement;
